// Data validation utilities for ensuring data integrity

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface ValidationRule<T> {
  validate: (value: T) => boolean;
  message: string;
}

// Generic validator class
export class Validator<T> {
  private rules: ValidationRule<T>[] = [];

  addRule(rule: ValidationRule<T>): Validator<T> {
    this.rules.push(rule);
    return this;
  }

  validate(value: T): ValidationResult {
    const errors: string[] = [];
    
    for (const rule of this.rules) {
      if (!rule.validate(value)) {
        errors.push(rule.message);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

// Common validation rules
export const ValidationRules = {
  required: <T>(message = 'This field is required'): ValidationRule<T> => ({
    validate: (value: T) => value !== null && value !== undefined && value !== '',
    message,
  }),

  minLength: (min: number, message?: string): ValidationRule<string> => ({
    validate: (value: string) => value.length >= min,
    message: message || `Must be at least ${min} characters long`,
  }),

  maxLength: (max: number, message?: string): ValidationRule<string> => ({
    validate: (value: string) => value.length <= max,
    message: message || `Must be no more than ${max} characters long`,
  }),

  email: (message = 'Must be a valid email address'): ValidationRule<string> => ({
    validate: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    message,
  }),

  url: (message = 'Must be a valid URL'): ValidationRule<string> => ({
    validate: (value: string) => {
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    message,
  }),

  number: (message = 'Must be a valid number'): ValidationRule<string> => ({
    validate: (value: string) => !isNaN(Number(value)) && isFinite(Number(value)),
    message,
  }),

  min: (min: number, message?: string): ValidationRule<number> => ({
    validate: (value: number) => value >= min,
    message: message || `Must be at least ${min}`,
  }),

  max: (max: number, message?: string): ValidationRule<number> => ({
    validate: (value: number) => value <= max,
    message: message || `Must be no more than ${max}`,
  }),

  array: (message = 'Must be an array'): ValidationRule<any> => ({
    validate: (value: any) => Array.isArray(value),
    message,
  }),

  nonEmptyArray: (message = 'Must be a non-empty array'): ValidationRule<any> => ({
    validate: (value: any) => Array.isArray(value) && value.length > 0,
    message,
  }),

  oneOf: <T>(options: T[], message?: string): ValidationRule<T> => ({
    validate: (value: T) => options.includes(value),
    message: message || `Must be one of: ${options.join(', ')}`,
  }),

  custom: <T>(validator: (value: T) => boolean, message: string): ValidationRule<T> => ({
    validate: validator,
    message,
  }),
};

// Specific validators for our application
export const AssessmentValidators = {
  clientName: new Validator<string>()
    .addRule(ValidationRules.required('Client name is required'))
    .addRule(ValidationRules.minLength(2, 'Client name must be at least 2 characters'))
    .addRule(ValidationRules.maxLength(100, 'Client name must be no more than 100 characters')),

  assessmentGoals: new Validator<Array<{id: string, text: string, priority: string}>>()
    .addRule(ValidationRules.array('Goals must be an array'))
    .addRule(ValidationRules.custom(
      (goals) => goals.every(goal => 
        typeof goal.id === 'string' && 
        typeof goal.text === 'string' && 
        ['high', 'medium', 'low'].includes(goal.priority)
      ),
      'Each goal must have valid id, text, and priority'
    )),

  questionResponse: new Validator<string>()
    .addRule(ValidationRules.required('Response is required')),

  email: new Validator<string>()
    .addRule(ValidationRules.required('Email is required'))
    .addRule(ValidationRules.email('Must be a valid email address')),
};

// API response validation
export const ApiResponseValidators = {
  assessment: new Validator<any>()
    .addRule(ValidationRules.custom(
      (data) => data && typeof data === 'object' && 'id' in data,
      'Assessment must have an id'
    )),

  question: new Validator<any>()
    .addRule(ValidationRules.custom(
      (data) => data && typeof data === 'object' && 'questionId' in data && 'questionText' in data,
      'Question must have questionId and questionText'
    )),

  response: new Validator<any>()
    .addRule(ValidationRules.custom(
      (data) => data && typeof data === 'object' && 'questionId' in data && 'answer' in data,
      'Response must have questionId and answer'
    )),
};

// Data sanitization utilities
export const DataSanitizers = {
  trimString: (value: string): string => value.trim(),
  
  sanitizeHtml: (value: string): string => {
    // Basic HTML sanitization - in production, use a library like DOMPurify
    return value
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  },

  sanitizeEmail: (value: string): string => {
    return value.toLowerCase().trim();
  },

  sanitizeUrl: (value: string): string => {
    try {
      const url = new URL(value);
      return url.toString();
    } catch {
      return '';
    }
  },

  sanitizeArray: <T>(value: any): T[] => {
    return Array.isArray(value) ? value : [];
  },
};

// Validation helper functions
export const validateAndSanitize = <T>(
  value: T,
  validator: Validator<T>,
  sanitizer?: (value: T) => T
): { result: ValidationResult; sanitizedValue: T } => {
  const sanitizedValue = sanitizer ? sanitizer(value) : value;
  const result = validator.validate(sanitizedValue);
  
  return {
    result,
    sanitizedValue,
  };
};

// Form validation hook
export const useFormValidation = <T extends Record<string, any>>(
  initialValues: T,
  validators: Partial<Record<keyof T, Validator<any>>>
) => {
  const [values, setValues] = React.useState<T>(initialValues);
  const [errors, setErrors] = React.useState<Partial<Record<keyof T, string[]>>>({});

  const validateField = (field: keyof T, value: any) => {
    const validator = validators[field];
    if (!validator) return { isValid: true, errors: [] };

    const result = validator.validate(value);
    setErrors(prev => ({
      ...prev,
      [field]: result.errors,
    }));

    return result;
  };

  const validateAll = () => {
    const newErrors: Partial<Record<keyof T, string[]>> = {};
    let isValid = true;

    for (const [field, validator] of Object.entries(validators)) {
      if (validator) {
        const result = validator.validate(values[field as keyof T]);
        if (!result.isValid) {
          newErrors[field as keyof T] = result.errors;
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    return { isValid, errors: newErrors };
  };

  const setValue = (field: keyof T, value: any) => {
    setValues(prev => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  return {
    values,
    errors,
    setValue,
    validateField,
    validateAll,
    isValid: Object.values(errors).every(errorList => !errorList || errorList.length === 0),
  };
};

// Import React for the hook
import React from 'react';
